import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Connector, TestConnectorResult } from "../lib/types";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge, Badge } from "../components/StatusBadge";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import { Loading, ErrorState, EmptyState } from "../components/EmptyState";

const STATUS_TONE = {
  active: "ok",
  pending: "warn",
  error: "danger",
} as const;

type TestOutcome = {
  ok: boolean;
  health?: TestConnectorResult["health"];
  title: string;
  nextStep?: string;
};

export function ConnectorsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.connectors.list(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.connectors.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  });

  const test = useMutation({
    mutationFn: (id: string) => api.connectors.test(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  });

  // Surface the latest test outcome for the row that was tested, so a failure
  // (e.g. "add a destination first") is readable instead of a console status.
  function outcomeFor(id: string): TestOutcome | undefined {
    if (test.variables !== id || test.isPending) return undefined;
    if (test.isError)
      return {
        ok: false,
        title: test.error instanceof Error ? test.error.message : "Test failed.",
      };
    if (test.data) {
      return {
        ok: test.data.ok,
        health: test.data.health,
        title: test.data.diagnosis.title,
        nextStep: test.data.diagnosis.nextStep,
      };
    }
    return undefined;
  }

  return (
    <div>
      <PageHeader
        title="Connections"
        description="Connect your own AWS or Azure storage. BlobShip brokers uploads using least-privilege, scoped, revocable access — your file bytes never transit our servers."
        action={
          <button className="btn-primary" onClick={() => navigate("/connect")}>
            New connection
          </button>
        }
      />

      {connectors.isLoading ? (
        <Loading />
      ) : connectors.isError ? (
        <ErrorState error={connectors.error} onRetry={connectors.refetch} />
      ) : connectors.data && connectors.data.length > 0 ? (
        <Table>
          <THead>
            <tr>
              <TH>Connection</TH>
              <TH>Cloud</TH>
              <TH>State</TH>
              <TH>Health</TH>
              <TH className="text-right">Actions</TH>
            </tr>
          </THead>
          <TBody>
            {connectors.data.map((c) => (
              <ConnectorRow
                key={c.id}
                connector={c}
                onOpen={() => navigate(`/connectors/${c.id}`)}
                onTest={() => test.mutate(c.id)}
                testing={test.isPending && test.variables === c.id}
                outcome={outcomeFor(c.id)}
                onRemove={() => {
                  if (confirm(`Remove connection "${c.displayName}"?`))
                    remove.mutate(c.id);
                }}
                removing={remove.isPending && remove.variables === c.id}
              />
            ))}
          </TBody>
        </Table>
      ) : (
        <EmptyState
          title="No connections yet"
          description="Connect your AWS or Azure storage to start brokering uploads into your own account."
          action={
            <button className="btn-primary" onClick={() => navigate("/connect")}>
              New connection
            </button>
          }
        />
      )}
    </div>
  );
}

function ConnectorRow({
  connector,
  onOpen,
  onTest,
  testing,
  outcome,
  onRemove,
  removing,
}: {
  connector: Connector;
  onOpen: () => void;
  onTest: () => void;
  testing: boolean;
  outcome?: TestOutcome;
  onRemove: () => void;
  removing: boolean;
}) {
  // Self-heal on view: a connector still settling after the wizard closed re-
  // checks itself once when the list loads, so it goes green on its own instead
  // of waiting for a manual Test — there is no background job to do it. The
  // reachable "still settling" states are status==='pending' (freshly created /
  // downgraded) and health==='unchecked' (an inconclusive probe writes
  // 'unchecked', never 'pending', to destinations — so gating on health
  // 'pending' would never fire). Debounced (60s) and one-shot per mount, so
  // healthy rows are never probed and reopening the list won't hammer the cloud.
  // A connector with no destination 409s here; that throws (no `auto.data`), so
  // `live` simply falls back to the persisted badge — nothing misleading shows.
  // Manual Test still takes precedence.
  const auto = useQuery({
    queryKey: ["connectorProbe", connector.id],
    queryFn: () => api.connectors.test(connector.id),
    enabled:
      (connector.status === "pending" || connector.health === "unchecked") &&
      !outcome,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const live: TestOutcome | undefined =
    outcome ??
    (auto.data
      ? {
          ok: auto.data.ok,
          health: auto.data.health,
          title: auto.data.diagnosis.title,
          nextStep: auto.data.diagnosis.nextStep,
        }
      : undefined);
  const effectiveHealth = live?.health ?? connector.health;

  return (
    <TR onClick={onOpen}>
      <TD>
        <div className="font-medium text-ink">{connector.displayName}</div>
        <div className="mono text-xs text-ink-subtle">
          {connector.cloud === "aws"
            ? connector.awsRoleArn
            : connector.azStorageAccount}
        </div>
        {live && (
          <div
            className={`mt-1 text-xs ${
              live.ok
                ? "text-ok"
                : live.health === "pending"
                  ? "text-ink-muted"
                  : "text-danger"
            }`}
          >
            {live.ok ? "✓ " : live.health === "pending" ? "◌ " : "✗ "}
            {live.title}
            {live.nextStep && (
              <span className="mt-0.5 block text-[11px] opacity-90">
                {live.nextStep}
              </span>
            )}
          </div>
        )}
      </TD>
      <TD>
        <span className="text-ink-muted">
          {connector.cloud === "aws"
            ? `AWS · ${connector.awsRegion ?? "—"}`
            : `Azure · ${connector.azTenantId ? "tenant set" : "—"}`}
        </span>
      </TD>
      <TD>
        <Badge tone={STATUS_TONE[connector.status]}>{connector.status}</Badge>
      </TD>
      <TD>
        <StatusBadge health={effectiveHealth} />
      </TD>
      <TD className="text-right">
        <div className="inline-flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button className="btn-secondary" onClick={onTest} disabled={testing}>
            {testing ? "Testing…" : "Test"}
          </button>
          <button className="btn-danger" onClick={onRemove} disabled={removing}>
            Remove
          </button>
        </div>
      </TD>
    </TR>
  );
}
